import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAmZmFilterOptions } from '@/hooks/useAmZmFilterOptions';
import { useDistrictThanaFilterOptions } from '@/hooks/useDistrictThanaFilterOptions';
import { withOptionCounts } from '@/utils/filters';
import {
  activeTeacherDesignationsQueryOptions,
  teacherCountsBySubmitterQueryOptions,
} from '../api/teacherSubmissions.api';
import type { TeacherCountsBySubmitterParams } from '../types/teacherSubmission.types';
import { getTeacherSubmissionFilterFields } from '../utils/teacherSubmissionFilters';

interface UseTeacherSubmissionFilterFieldsParams {
  isEnabled: boolean;
  selectedInstituteId: number | undefined;
  selectedInstituteName: string;
  districtId: number | undefined;
  amUserId: number | undefined;
  countsBySubmitterParams: TeacherCountsBySubmitterParams;
}

export function useTeacherSubmissionFilterFields({
  isEnabled,
  selectedInstituteId,
  selectedInstituteName,
  districtId,
  amUserId,
  countsBySubmitterParams,
}: UseTeacherSubmissionFilterFieldsParams) {
  const { data: designations, isError: hasDesignationsError } = useQuery({
    ...activeTeacherDesignationsQueryOptions(),
    enabled: isEnabled,
  });
  const {
    amOptions,
    zmOptions,
    hasOptionsError: hasHierarchyOptionsError,
  } = useAmZmFilterOptions({ isEnabled, amUserId });
  const {
    districts,
    thanas,
    hasOptionsError: hasGeoOptionsError,
  } = useDistrictThanaFilterOptions({ isEnabled, districtId });
  const { data: countsBySubmitter, isError: hasCountsError } = useQuery({
    ...teacherCountsBySubmitterQueryOptions(countsBySubmitterParams),
    enabled: isEnabled,
  });

  const fields = useMemo(
    () =>
      getTeacherSubmissionFilterFields({
        selectedInstitute:
          selectedInstituteId === undefined
            ? null
            : { label: selectedInstituteName, value: String(selectedInstituteId) },
        hasDistrict: districtId !== undefined,
        districts,
        thanas,
        designations,
        amOptions: withOptionCounts(amOptions, countsBySubmitter?.amCounts),
        submitterOptions: withOptionCounts(zmOptions, countsBySubmitter?.zmCounts),
      }),
    [
      selectedInstituteId,
      selectedInstituteName,
      districtId,
      districts,
      thanas,
      designations,
      amOptions,
      zmOptions,
      countsBySubmitter,
    ],
  );

  return {
    fields,
    hasOptionsError:
      hasDesignationsError || hasHierarchyOptionsError || hasGeoOptionsError || hasCountsError,
  };
}
